import React, { useState, useEffect, ReactElement } from 'react';
import { useAccountOverview } from '../../../../../features/account/account-overview/account-overview.hook';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { coreService } from '../../../../../services/core/core-service';
import { Summarization } from '../../../../../services/types';
import {
  Button,
  Collapse,
  Divider,
  Dropdown,
  Empty,
  Flex,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { format } from 'date-fns'; //isSameDay, isSameMonth, isSameYear
import { MeetingTitleLink } from '../../../../../components/common/MeetingTitleLink';

import Styles from './style';
import { CaretRightOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { useToast } from '../../../../../hooks/useToast';
const { Container, EmptyState, Text } = Styles;

type SummaryHistoryProps = {
  summarization: Summarization;
  isProcessing: boolean;
  processType: 'deleting' | 'summarizing';
  onDelete: (summaryHistoryItemId: string) => void;
};

const Item: React.FC<SummaryHistoryProps> = ({
  summarization,
  isProcessing,
  processType,
  onDelete,
}) => {
  if (summarization.narrative?.length === 0) return <></>;
  return (
    <>
      <Collapse
        bordered={false}
        style={{ width: '100%', background: '#fefefe', border: '1px solid #e5e7eb' }}
        collapsible={isProcessing ? 'disabled' : 'header'}
        expandIcon={({ isActive }) => (
          <Flex gap={8}>
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
            {isProcessing && (
              <Tooltip
                title={`AI summary is being ${
                  processType === 'deleting' ? 'deleted' : 'generated'
                }`}
              >
                <Spin size="small" />
              </Tooltip>
            )}
          </Flex>
        )}
        items={[
          {
            key: '1',
            label: (
              <Flex gap={8}>
                <Tag color="orange">
                  {summarization.events.length} meeting{summarization.events.length > 1 ? 's' : ''}
                </Tag>
                <Flex gap={4}>
                  Generated on {format(new Date(summarization.lastRequestedAt), 'MM/dd/yyyy')}
                </Flex>
              </Flex>
            ),
            extra: (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => onDelete(summarization.id),
                    },
                  ],
                }}
              >
                <Button icon={<MoreOutlined />} size="small" />
              </Dropdown>
            ),
            children: (
              <div>
                <Typography.Title level={4} style={{ marginTop: 0 }}>
                  Meetings
                </Typography.Title>
                <ul style={{ paddingInlineStart: 16 }}>
                  {summarization.events.map(meeting => (
                    <li key={meeting.id}>
                      <MeetingTitleLink
                        id={meeting.id}
                        title={meeting.title}
                        date={meeting.scheduledStartDate}
                      />
                    </li>
                  ))}
                </ul>

                <Divider />

                <Typography.Title level={4}>Summarization</Typography.Title>

                <Text>
                  {isProcessing ? (
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                      }}
                    >
                      AI summary is being {processType === 'deleting' ? 'deleted' : 'generated'}
                      <Spin size="small" />
                    </div>
                  ) : (
                    summarization.narrative
                  )}
                </Text>
              </div>
            ),
          },
        ]}
      />
    </>
  );
};

export const SummaryHistory: React.FC<{ active: boolean }> = ({ active }) => {
  const { selectedAccount } = useAccountOverview();
  const { error, success } = useToast();
  const queryClient = useQueryClient();

  const [summarization, setSummarization] = useState<Summarization[]>([]);
  const [summarizationItem, setSummarizationItem] = useState<Summarization>();

  const { data, isFetching, isRefetching } = useQuery({
    queryKey: ['user-event-summarization', selectedAccount.id, active],
    queryFn: () => {
      return coreService.getAccountEventSummarizations(selectedAccount.id);
    },
    refetchIntervalInBackground: true,
    refetchInterval: 15000,
  });

  const handleDelete = async (id: string) => {
    try {
      setSummarizationItem(summarization.find(item => item.id === id));
      await coreService.deleteAccountEventSummarization(selectedAccount.id, id);
      setSummarization(prev => prev.filter(item => item.id !== id));
      success('Item deleted successfully.');
      queryClient.refetchQueries({
        queryKey: ['user-event-summarization', selectedAccount.id, active],
      });
    } catch (err) {
      error('Something went wrong when deleting the Summary History item.');
    } finally {
      setSummarizationItem(undefined);
    }
  };

  const items: ReactElement[] = summarization.map(summarization => (
    <Item
      key={summarization.id}
      summarization={summarization}
      onDelete={handleDelete}
      isProcessing={summarization.isProcessing || summarizationItem?.id === summarization.id}
      processType={summarization.isProcessing ? 'summarizing' : 'deleting'}
    />
  ));

  useEffect(() => {
    const sortedData = data ? [...data?.summarizations] : [];
    sortedData.sort(
      (a, b) => new Date(b.lastRequestedAt).getTime() - new Date(a.lastRequestedAt).getTime(),
    );
    setSummarization(sortedData);
  }, [data]);

  useEffect(() => {
    return;
  }, []);

  return (
    <Container>
      {isFetching && !isRefetching && (
        <Flex justify="center" align="center" style={{ width: '100%', height: '100%' }}>
          <Spin />
        </Flex>
      )}
      {summarization.length === 0 && !isFetching ? (
        <EmptyState>
          <Empty
            image={Empty.PRESENTED_IMAGE_DEFAULT}
            description={
              <>
                <span>No AI Summarizations Generated. </span>
                <br />
                <span>
                  To Generate a new AI Summary, select a few meetings from the Upcoming & Past
                  Meetings tab.
                </span>
              </>
            }
          />
        </EmptyState>
      ) : (
        items
      )}
    </Container>
  );
};
